import { callMlsWorker, terminateMlsWorker } from './mlsWorkerEntry.js';

const toBase64 = (bytes) => {
  if (typeof Buffer !== 'undefined' && typeof Buffer.from === 'function') {
    return Buffer.from(bytes).toString('base64');
  }

  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }

  if (typeof btoa === 'function') {
    return btoa(binary);
  }

  return globalThis?.Buffer?.from ? globalThis.Buffer.from(bytes).toString('base64') : binary;
};

const coerceBase64 = (value) => {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || null;
  }

  if (value instanceof Uint8Array) {
    return toBase64(value);
  }

  if (ArrayBuffer.isView(value)) {
    return toBase64(new Uint8Array(value.buffer, value.byteOffset, value.byteLength));
  }

  if (value instanceof ArrayBuffer) {
    return toBase64(new Uint8Array(value));
  }

  return null;
};

const coerceUint8Array = (value) => {
  if (!value) {
    return null;
  }
  if (value instanceof Uint8Array) {
    return value;
  }
  if (ArrayBuffer.isView(value)) {
    return new Uint8Array(value.buffer, value.byteOffset, value.byteLength);
  }
  if (value instanceof ArrayBuffer) {
    return new Uint8Array(value);
  }
  return null;
};

const buildCompatInitPayload = (args) => {
  if (!Array.isArray(args) || args.length === 0) {
    return {};
  }

  if (args.length === 1 && (typeof args[0] === 'number' || typeof args[0] === 'string')) {
    return { preferredCiphersuite: args[0] };
  }

  const [identityPrivateKey, credential, ciphersuite, storage, options] = args;
  return {
    identityPrivateKey,
    credential,
    ciphersuite,
    storage,
    options,
  };
};

const sanitizeInitPayload = (payload) => {
  const normalized = payload && typeof payload === 'object' && !Array.isArray(payload)
    ? { ...payload }
    : {};

  Object.keys(normalized).forEach((key) => {
    if (normalized[key] === undefined) {
      delete normalized[key];
    }
  });

  return normalized;
};

const INIT_RETRY_DELAY_MS = 500;
const INIT_RETRY_TIMEOUT_MS = 120000;

const isTransientWorkerInitError = (error = null) => {
  const code = String(error?.code || '').trim().toUpperCase();
  const message = String(error?.message || '').toLowerCase();
  return (
    code === 'MLS_WORKER_CALL_TIMEOUT' ||
    code === 'MLS_WORKER_RESTARTED' ||
    message.includes('mls worker call timed out') ||
    message.includes('mls_worker_restarted')
  );
};

const delay = (ms) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

async function initMlsWithRecovery(payload) {
  let lastError = null;

  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      return await callMlsWorker('init', payload, {
        restartOnTimeout: true,
        timeoutMs: attempt === 0 ? undefined : INIT_RETRY_TIMEOUT_MS,
      });
    } catch (error) {
      lastError = error;
      if (!isTransientWorkerInitError(error) || attempt === 1) {
        throw error;
      }

      await delay(INIT_RETRY_DELAY_MS);
    }
  }

  throw lastError;
}

export async function initMls(...args) {
  let payload = {};
  if (args.length === 0) {
    payload = {};
  } else if (args.length === 1 && args[0] && typeof args[0] === 'object' && !Array.isArray(args[0])) {
    payload = sanitizeInitPayload(args[0]);
  } else if (args[0] && typeof args[0] === 'object' && !Array.isArray(args[0])) {
    if (typeof console?.warn === 'function') {
      try {
        console.warn('[MLS] initMls called with deprecated positional arguments; please pass an options object.');
      } catch {}
    }
    const compat = sanitizeInitPayload(buildCompatInitPayload(args.slice(1)));
    payload = { ...sanitizeInitPayload(args[0]), ...compat };
  } else {
    if (typeof console?.warn === 'function') {
      try {
        console.warn('[MLS] initMls called with deprecated positional arguments; please pass an options object.');
      } catch {}
    }
    payload = sanitizeInitPayload(buildCompatInitPayload(args));
  }

  return initMlsWithRecovery(payload);
}

export async function createIdentity({ name, ciphersuite, seed, identityId } = {}) {
  if (!name) throw new Error('name is required');
  return callMlsWorker('createIdentity', { name, ciphersuite, seed, identityId });
}

export async function previewIdentityKeyPackage({ name, ciphersuite, seed, identityId } = {}) {
  if (!name) throw new Error('name is required');
  return callMlsWorker('previewIdentityKeyPackage', { name, ciphersuite, seed, identityId });
}

export async function createGroup({ identityId, groupId, ciphersuite } = {}) {
  if (!identityId) throw new Error('identityId is required');
  return callMlsWorker('createGroup', { identityId, groupId, ciphersuite });
}

export async function exportKeyPackage({ identityId, refreshIfConsumed = false } = {}) {
  return callMlsWorker('exportKeyPackage', { identityId, refreshIfConsumed });
}

export async function rotateKeyPackage({ identityId, ciphersuite } = {}) {
  if (!identityId) throw new Error('identityId is required');
  return callMlsWorker('rotateKeyPackage', { identityId, ciphersuite });
}

export async function debugIdentityState({ identityId, includeKeyPackage = false } = {}) {
  return callMlsWorker(
    'debugIdentityState',
    { identityId, includeKeyPackage },
    {
      timeoutMs: 7000,
      restartOnTimeout: false,
    },
  );
}

export async function getIdentityHealth({ identityId, includeKeyPackage = false } = {}) {
  return callMlsWorker(
    'getIdentityHealth',
    { identityId, includeKeyPackage },
    {
      timeoutMs: 7000,
      restartOnTimeout: false,
    },
  );
}

export async function getKeyPackageHealth({ identityId, includeKeyPackage = false } = {}) {
  return callMlsWorker(
    'getKeyPackageHealth',
    { identityId, includeKeyPackage },
    {
      timeoutMs: 7000,
      restartOnTimeout: false,
    },
  );
}

export async function debugGroupRuntimeState({
  groupHandle,
  includeExportKey = true,
  label,
  context,
  contextB64,
  length,
} = {}) {
  if (!groupHandle) throw new Error('groupHandle is required');
  return callMlsWorker('debugGroupRuntimeState', {
    groupHandle,
    includeExportKey,
    label,
    context,
    contextB64,
    length,
  }, {
    timeoutMs: 7000,
    // Debug timeouts should never reset MLS runtime state.
    restartOnTimeout: false,
  });
}

export async function debugDumpRuntimeState({
  includeExportKey = true,
  includeKeyPackage = false,
  label,
  context,
  contextB64,
  length,
} = {}) {
  return callMlsWorker('debugDumpRuntimeState', {
    includeExportKey,
    includeKeyPackage,
    label,
    context,
    contextB64,
    length,
  }, {
    timeoutMs: 9000,
    // Debug timeouts should never reset MLS runtime state.
    restartOnTimeout: false,
  });
}

export async function listGroups() {
  const result = await callMlsWorker(
    'listGroups',
    undefined,
    {
      // listGroups is frequently called during warm-up/sync while worker can be busy.
      // Avoid restarting worker on observational timeout to prevent runtime group loss.
      timeoutMs: 12000,
      restartOnTimeout: false,
    },
  );
  return result?.groups || [];
}

export async function dropRuntimeGroupsByGroupId({ groupId, keepHandle = null } = {}) {
  if (!groupId) throw new Error('groupId is required');
  return callMlsWorker('dropRuntimeGroupsByGroupId', { groupId, keepHandle });
}

export async function removeRuntimeGroup({ groupHandle } = {}) {
  if (!groupHandle) throw new Error('groupHandle is required');
  return callMlsWorker('removeRuntimeGroup', { groupHandle });
}

export async function exportGroupKey({
  groupHandle,
  label,
  context,
  contextB64,
  length,
} = {}) {
  if (!groupHandle) throw new Error('groupHandle is required');
  return callMlsWorker('exportGroupKey', {
    groupHandle,
    label,
    context,
    contextB64,
    length,
  });
}

export async function joinGroup({
  identityId,
  welcome,
  welcomeB64,
  ratchetTree,
  ratchetTreeB64,
  ratchetTreeBytes,
  ratchetTreeBuffer,
  groupId,
  allowIdentityRestore,
} = {}) {
  const normalizedWelcomeB64 = coerceBase64(welcomeB64 ?? welcome);
  if (!normalizedWelcomeB64) {
    throw new Error('welcome payload is required');
  }

  const normalizedRatchetTreeB64 = coerceBase64(ratchetTreeB64 ?? ratchetTree);
  const normalizedRatchetTreeBytes =
    coerceUint8Array(ratchetTreeBytes)
    || coerceUint8Array(ratchetTreeBuffer)
    || (typeof ratchetTree !== 'string' ? coerceUint8Array(ratchetTree) : null);

  const payload = {
    identityId,
    groupId,
    welcomeB64: normalizedWelcomeB64,
  };

  if (normalizedRatchetTreeB64) {
    payload.ratchetTreeB64 = normalizedRatchetTreeB64;
  }

  if (normalizedRatchetTreeBytes) {
    payload.ratchetTreeBytes = normalizedRatchetTreeBytes;
  }

  if (typeof allowIdentityRestore === 'boolean') {
    payload.allowIdentityRestore = allowIdentityRestore;
  }

  return callMlsWorker('joinGroup', payload, {
    // Session manager already wraps join with a stricter race timeout and retry strategy.
    // Avoid a second timeout watchdog here that can restart worker late for an abandoned race.
    timeoutMs: 45000,
    restartOnTimeout: false,
  });
}

export async function addMemberFromKeyPackageBytes({
  groupHandle,
  identityId,
  keyPackageB64,
} = {}) {
  if (!groupHandle) throw new Error('groupHandle is required');
  if (!keyPackageB64) throw new Error('keyPackageB64 is required');
  return callMlsWorker('addMemberFromKeyPackageBytes', {
    groupHandle,
    identityId,
    keyPackageB64,
  });
}

export async function encryptMessage({ groupHandle, identityId, plaintext } = {}) {
  if (!groupHandle) throw new Error('groupHandle is required');
  if (!identityId) throw new Error('identityId is required');
  return callMlsWorker('encrypt', { groupHandle, identityId, plaintext });
}

export async function decryptMessage({ groupHandle, ciphertext, epochHint } = {}) {
  if (!groupHandle) throw new Error('groupHandle is required');
  if (!ciphertext) throw new Error('ciphertext is required');
  return callMlsWorker('decrypt', { groupHandle, ciphertext, epochHint });
}

export async function buildBootstrapProbeEnvelope({
  identityId,
  groupId,
  senderClientId,
  clientMsgId,
  ciphersuite,
  epoch,
  conversationId,
  payload,
  timestamp,
} = {}) {
  const normalizeString = (value) => {
    if (typeof value !== 'string') {
      return null;
    }
    const trimmed = value.trim();
    return trimmed || null;
  };

  const normalizedIdentityId = normalizeString(identityId);
  const normalizedGroupId = normalizeString(groupId);
  const normalizedSenderClientId = normalizeString(senderClientId);
  const normalizedClientMsgId = normalizeString(clientMsgId);

  if (!normalizedIdentityId || !normalizedGroupId || !normalizedSenderClientId || !normalizedClientMsgId) {
    return null;
  }

  const payloadEnvelope = {};
  payloadEnvelope.identityId = normalizedIdentityId;
  payloadEnvelope.groupId = normalizedGroupId;
  payloadEnvelope.senderClientId = normalizedSenderClientId;
  payloadEnvelope.clientMsgId = normalizedClientMsgId;

  if (ciphersuite != null) {
    payloadEnvelope.ciphersuite = ciphersuite;
  }
  if (epoch != null) {
    payloadEnvelope.epoch = epoch;
  }
  const normalizedConversationId = normalizeString(conversationId);
  if (normalizedConversationId != null) {
    payloadEnvelope.conversationId = normalizedConversationId;
  }
  if (timestamp != null) {
    payloadEnvelope.timestamp = timestamp;
  }
  if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
    payloadEnvelope.payload = { ...payload };
  }

  const result = await callMlsWorker('buildBootstrapProbeEnvelope', payloadEnvelope);
  const envelopeB64 =
    coerceBase64(result?.envelopeB64)
    || coerceBase64(result?.envelope_b64)
    || coerceBase64(result?.bytes)
    || coerceBase64(result);
  return envelopeB64;
}

export function shutdownMlsWorker() {
  terminateMlsWorker();
}

export async function getSupportedCiphersuites() {
  const result = await callMlsWorker('getSupportedCiphersuites');
  if (Array.isArray(result?.suites)) {
    return result.suites;
  }
  if (Array.isArray(result)) {
    return result;
  }
  return [];
}

export async function decodeWelcomeCiphersuite(input) {
  let welcome = null;
  if (input instanceof ArrayBuffer) {
    welcome = input;
  } else if (ArrayBuffer.isView(input)) {
    welcome = input.buffer.slice(input.byteOffset, input.byteOffset + input.byteLength);
  } else if (input?.welcome) {
    const value = input.welcome;
    if (value instanceof ArrayBuffer) {
      welcome = value;
    } else if (ArrayBuffer.isView(value)) {
      welcome = value.buffer.slice(value.byteOffset, value.byteOffset + value.byteLength);
    }
  }

  const payload = {};
  if (welcome) {
    payload.welcome = welcome;
  }
  if (input?.welcomeB64) {
    payload.welcomeB64 = input.welcomeB64;
  }

  const result = await callMlsWorker('decodeWelcomeCiphersuite', payload);
  return {
    ciphersuite: typeof result?.ciphersuite === 'number' ? result.ciphersuite : null,
    error: result?.error ? new Error(result.error) : null,
  };
}
